import { type TellescopeModelFromName } from "../../mappings";
import { useTellescopeInfiniteQuery } from "../use-tellescope-infinite-query";

/**
 * Fetches users from the Tellescope API with optional filtering by user IDs.
 *
 * This hook uses infinite query pagination to efficiently load user records.
 * When userIds are provided, it filters the results to only include matching users.
 * When userIds is undefined, it loads all users.
 *
 * @param userIds - Optional array of user IDs to filter by. If provided and empty,
 *                  the query is disabled. If undefined, all users are loaded.
 * @param enabled - Optional boolean to enable/disable query. Defaults to enabled.
 *
 * @returns An infinite query result containing:
 *   - data: Array of user records (filtered by userIds if provided)
 *   - isLoading: Loading state indicator
 *   - error: Error object if the query failed
 *   - fetchNextPage: Function to load more pages
 *   - hasNextPage: Whether more pages are available
 *
 * @example
 * ```tsx
 * // Load all users
 * const { data: allUsers, isLoading } = useUsersQuery();
 *
 * // Load specific users by ID
 * const { data: specificUsers } = useUsersQuery(['user1', 'user2']);
 *
 * // Query disabled when empty array provided
 * const { data } = useUsersQuery([]); // Query will not execute
 * ```
 *
 * @remarks
 * - Uses cursor-based pagination with a limit of 1000 records per page
 * - Automatically disabled when an empty userIds array is provided
 * - Client-side filtering is applied when userIds are specified
 */
const USERS_PAGE_LIMIT = 1000;

export const useUsersQuery = (userIds?: string[], enabled: boolean = true) => {
  return useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: { limit: USERS_PAGE_LIMIT },
    // Stop paginating once a page comes back short: a page smaller than the requested limit is the
    // last one. The default getSome cursor only stops on a fully empty page, which requires one
    // extra round-trip and never terminates against a source that returns a full page for every
    // cursor — that spins `fetchAllPages` and leaves the query perpetually refetching.
    getNextPageParam: (lastPage) => (lastPage.length < USERS_PAGE_LIMIT ? undefined : lastPage.at(-1)?.id),
    select: (data) =>
      data.pages.reduce<TellescopeModelFromName<"users">[]>((acc, page) => {
        if (userIds?.length) {
          acc.push(...page.filter((user) => userIds.includes(user.id)));
        } else {
          acc.push(...page);
        }
        return acc;
      }, []),
    enabled: enabled && (userIds === undefined || userIds.length > 0),
    fetchAllPages: !userIds?.length,
  });
};
